import { Component, OnInit, ViewChild, inject } from '@angular/core';

import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, NgForm, ReactiveFormsModule } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { CategoryUserGroups, Categories, UserGroupPermissions, UserGroups, Permissions } from '@parashift/shared/constants';
import { SessionService } from '@parashift/shared/services';
import { IterateObjectPipe } from '@parashift/shared/pipes';

@Component({
  selector: 'edit-permissions-modal',
  templateUrl: './edit-permissions-modal.component.html',
  styleUrls: ['./edit-permissions-modal.component.scss'],
  imports: [ReactiveFormsModule, IterateObjectPipe]
})
export class EditPermissionsModalComponent implements OnInit {
  bsModalRef = inject(BsModalRef);
  private formBuilder = inject(UntypedFormBuilder);
  private sessionService = inject(SessionService);

  @ViewChild('editPermissionsForm') set epForm(epForm: NgForm) {
     if (epForm) {
         this.editPermissionsForm = epForm;
     }
  }

  private allowedPermissions: string[];
  private currentUserGroups: string[];
  private currentPermissions: string[];
  private readonly categoryUserGroups = CategoryUserGroups;
  private readonly userGroupPermissions = UserGroupPermissions;
  readonly categories = Categories;
  readonly userGroups = UserGroups;
  readonly permissions = Permissions;

  formCategoryUserGroups = {};
  formUserGroupPermissions = {};

  globalAccess = false;
  isTenantOwner = false;
  isTenantAdmin = false;

  editPermissionsForm: NgForm;
  state: boolean;
  showForm = false;
  form: UntypedFormGroup;
  fullPermission = false;
  isAdmin = false;
  isOwner = false;
  isUserManagerSelfEditing = false;
  disableSubmit = true;

  get email() { return this.form.get('email'); }

  ngOnInit() {
    setTimeout(() => {
      this.checkCurrentUserPermissions();
      this.buildForm();
      this.checkGlobalAccess();
      this.initCurrentUsersPermissions();
      this.initModelValues();
      this.initGroupsAndPermissions();
      this.setCurrentUsergroups();
      this.showForm = true;
    });
  }

  private checkGlobalAccess() {
    if (this.sessionService.userIsAdminSupportOrSales) {
      this.globalAccess = true;
    }
  }

  private initCurrentUsersPermissions() {
    this.isTenantOwner = this.bsModalRef.content.data.editorPermissions.indexOf('owner') !== -1;
    this.isTenantAdmin = this.bsModalRef.content.data.editorPermissions.indexOf('admin') !== -1;
  }

  onCheckboxChange(target: EventTarget) {
    const checked = (target as HTMLInputElement).checked;
    const value = (target as HTMLInputElement).value;

    if (value === 'admin' || value === 'owner') {
      this.toggleAdminPermissions(checked, value);
    } else if (checked) {
      this.addUserGroup(value);
    } else {
      this.removeUserGroup(value);
    }

    this.checkuserGroups();
  }

  submitForm() {
    if (this.form.valid) {
      let permissions = [];
      const usergroups = [];

      this.form.value.userGroups.forEach((userGroup: string) => {
        if (userGroup.includes('pdc_group.')) {
          usergroups.push(userGroup);
          if (this.formUserGroupPermissions[userGroup]) {
            permissions = [...permissions, ...this.formUserGroupPermissions[userGroup]];
          } else if (userGroup === 'admin' || userGroup === 'owner') {
            permissions = [...permissions, userGroup];
          }
        } else {
          permissions.push(userGroup);
        }
      });

      permissions = permissions.reduce((unique: string[], permission: string) => {
        return unique.includes(permission) ? unique : [...unique, permission];
      }, []);

      this.bsModalRef.content.model.user_permissions = this.applyPermissions(permissions);
      this.bsModalRef.content.model.user_groups = usergroups;
      this.state = true;
      this.bsModalRef.hide();
    }
  }

  close() {
    this.bsModalRef.content.state = false;
    this.bsModalRef.hide();
  }

  private buildForm() {
    this.form = this.formBuilder.group({
      userGroups: this.formBuilder.array([])
    });
  }

  private initModelValues() {
    this.allowedPermissions = (this.bsModalRef.content.model.tenant_permissions || []).map(tp => tp);
    this.currentUserGroups = (this.bsModalRef.content.model.user_groups || []).map(ug => ug);
    this.currentPermissions = (this.bsModalRef.content.model.user_permissions || []).map(up => up);
  }

  private initGroupsAndPermissions() {
    if (this.allowedPermissions.some(p => p.includes('pdc.'))) {
      for (const category of Object.keys(this.categoryUserGroups)) {
        this.formCategoryUserGroups[category] = [];
        this.categoryUserGroups[category].forEach(userGroup => {
          this.formCategoryUserGroups[category].push(userGroup);

          if (userGroup !== 'admin' && userGroup !== 'owner') {
            this.formUserGroupPermissions[userGroup] = [];
            this.userGroupPermissions[userGroup].forEach(permission => {
              if (this.allowedPermissions.includes(permission)) {
                this.formUserGroupPermissions[userGroup].push(permission);
              }
            });

            if (this.formUserGroupPermissions[userGroup].length === 0) {
              delete this.formUserGroupPermissions[userGroup];
            }
          }
        });

        if (this.formCategoryUserGroups[category].length === 0) {
          delete this.formCategoryUserGroups[category];
        }
      }

      if (this.currentPermissions.includes('admin')) {
        this.currentUserGroups.push('admin');
      }

      if (this.currentPermissions.includes('owner')) {
        this.currentUserGroups.push('owner');
      }
    }
  }

  private setCurrentUsergroups() {
    this.currentUserGroups.forEach(userGroup => {
      this.addUserGroup(userGroup);
      if (userGroup === 'admin' || userGroup === 'owner') {
        this.isAdmin = userGroup === 'admin' ? true : false;
        this.isOwner = userGroup === 'owner' ? true : false;
        this.fullPermission = true;
      }
    });

    this.checkuserGroups();
  }

  private toggleAdminPermissions(checked: boolean, value: string) {
    const userGroups: UntypedFormArray = this.form.get('userGroups') as UntypedFormArray;
    this.isAdmin = (checked && value === 'admin') || (this.form.value.userGroups.includes('admin') && value !== 'admin');
    this.isOwner = (checked && value === 'owner') || (this.form.value.userGroups.includes('owner') && value !== 'owner');

    if (checked) {
      userGroups.push(new UntypedFormControl(value));

      for (let i = userGroups.controls.length - 1; i >= 0; i--) {
        if (userGroups.controls[i].value !== 'admin' && userGroups.controls[i].value !== 'owner') {
          userGroups.removeAt(i);
        }
      }
      this.fullPermission = true;
    } else {
      this.removeUserGroup(value);

      if (!this.form.value.userGroups.includes('admin') && !this.form.value.userGroups.includes('owner')) {
        this.fullPermission = false;
      }
    }
  }

  private addUserGroup(value: string) {
    const userGroups: UntypedFormArray = this.form.get('userGroups') as UntypedFormArray;
    userGroups.push(new UntypedFormControl(value));
  }

  private removeUserGroup(value: string) {
    const userGroups: UntypedFormArray = this.form.get('userGroups') as UntypedFormArray;
    const index = userGroups.controls.findIndex(control => control.value === value);

    if (index > -1) {
      userGroups.removeAt(index);
    }
  }

  private applyPermissions(newPermissions: string[]) {
    let currentPermissions = this.bsModalRef.content.model.user_permissions.filter(p => !p.includes('pdc.') && !p.includes('admin') && !p.includes('owner'));

    if (newPermissions.includes('admin') || newPermissions.includes('owner')) {
      currentPermissions = [];
    }

    return [...currentPermissions, ...newPermissions];
  }

  private checkuserGroups() {
    const userGroups = this.form.get('userGroups');

    if (userGroups['length'] > 0) {
      this.disableSubmit = false;
    } else {
      this.disableSubmit = true;
    }
  }

  private checkCurrentUserPermissions() {
    // If there is no user it means we are editing an invite instead of existing users
    if (!this.bsModalRef.content.model.user) {
      return;
    }

    if (
      this.sessionService.user_id === this.bsModalRef.content.model.user.id
      && this.bsModalRef.content.model.user_permissions.includes('pdc.mng_tenant_users')
    ) {
      this.isUserManagerSelfEditing = true;
    }
  }
}
